import { useLocalStorage } from "@/lib/storage";

import Submenu from "./SubMenu";

import {
  MenuWorkbooks,
  WorkbookInfo,
  WorkbooksByMenu,
  TableInfo,
  Tables,
  TablesByMenu,
} from "@/features/Auth";
import {
  SectionRouteOverrides,
  SectionTranscription,
} from "@/features/Auth/user";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { ChevronDown } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useWorkspace } from "@/features/Workspaces/WorkspaceContext";
import { useEffect, useMemo } from "react";
import {
  getWorkspaceCodeFromUrl,
  normalizeCode,
} from "@/features/Workspaces/deriveWorkspaces";
import { matchPath, useLocation } from "react-router-dom";
import { cn } from "@heroui/theme";
import { useScrollMenuHeaderToTop } from "./useScrollMenuHeaderToTop";
import { useRedirectIfNotAllowed } from "./useRedirectIfNotAllowed";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_WORKSPACE_GROUP_KEY,
  normalizeWorkspaceGroupKey,
} from "@/features/Workspaces/clientGroups";
import {
  buildWorkspaceAdminPath,
  buildWorkspaceDashboardPath,
  normalizeWorkspaceGroupForPath,
  parseWorkspaceContextFromPathname,
} from "@/features/Workspaces/workspaceRoutes";

const K1_GROUPS: Array<{ label: string; routeItems: string[] }> = [
  {
    label: "PRODUCTION",
    routeItems: [
      "k1_data_upload",
      "k1_documents",
      "k1_email_logs",
      "k1_funds_portfolio",
      "k1_raw_data",
      "k1_reconciliation",
      "k1_positions_issuers_reconciliation",
    ],
  },
  {
    label: "GOLDEN DATA — FUNDS",
    routeItems: [
      "k1_management_companies",
      "k1_pe_funds",
      "k1_capital_account",
    ],
  },
  {
    label: "GOLDEN DATA — PORTFOLIO",
    routeItems: [
      "k1_issuers",
      "k1_positions",
      "k1_positions_issuers",
      "k1_mastersheet",
    ],
  },
  {
    label: "ANALYTICS",
    routeItems: ["k1_analytics", "k1_level_analytics"],
  },
];

const R1_GROUPS: Array<{ label: string; routeItems: string[] }> = [
  {
    label: "PRODUCTION",
    routeItems: [
      "r1_data_upload",
      "r1_documents",
      "r1_email_logs",
      "r1_funds_portfolio",
      "r1_raw_data",
      "r1_reconciliation",
      "r1_positions_issuers_reconciliation",
    ],
  },
  {
    label: "GOLDEN DATA — FUNDS",
    routeItems: [
      "r1_management_companies",
      "r1_pe_funds",
      "r1_capital_account",
    ],
  },
  {
    label: "GOLDEN DATA — PORTFOLIO",
    routeItems: [
      "r1_issuers",
      "r1_positions",
      "r1_positions_issuers",
      "r1_mastersheet",
    ],
  },
  {
    label: "ANALYTICS",
    routeItems: ["r1_analytics", "r1_level_analytics"],
  },
];

type MenuProps = {
  userWorkooks: MenuWorkbooks;
  isSidebarClose: boolean;
  userTables: Tables;
  sectionTranscription: SectionTranscription;
  sectionRouteOverrides: SectionRouteOverrides;
  hideMenuTitles?: boolean;
  //isQuantilia: boolean;
};

type MenuStatesType = {
  [menuName: string]: boolean;
};

function normalizeGroupKey(rawGroup: string | null | undefined): string {
  return (
    normalizeWorkspaceGroupKey(rawGroup, { fallbackToDefault: true }) ??
    DEFAULT_WORKSPACE_GROUP_KEY
  );
}

function getMatchingGroupKeys<T extends Record<string, unknown>>(
  source: T,
  requestedGroup: string,
): Array<keyof T> {
  const normalized = normalizeGroupKey(requestedGroup);
  return (Object.keys(source) as Array<keyof T>).filter((key) => {
    return normalizeGroupKey(String(key)) === normalized;
  });
}

function findUrlGroup(
  pathname: string,
  urlCode: string,
  userWorkooks: MenuWorkbooks,
  userTables: Tables,
): string | undefined {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] === "w" && parts[1]) {
    return normalizeGroupKey(parts[1]);
  }
  if (parts[0] === "dashboard" && parts[1]) {
    const uuid = parts[1];
    for (const [groupName, byWorkspace] of Object.entries(userWorkooks)) {
      const normalizedGroup = normalizeGroupKey(groupName);
      for (const [workspaceName, byMenu] of Object.entries(byWorkspace)) {
        if (normalizeCode(workspaceName) !== normalizeCode(urlCode)) continue;
        for (const entries of Object.values(byMenu)) {
          if (uuid in entries) {
            return normalizedGroup;
          }
        }
      }
    }
  }

  if (parts[0] === "admin") {
    for (const [groupName, byMenu] of Object.entries(userTables)) {
      const normalizedGroup = normalizeGroupKey(groupName);
      for (const sectionName of Object.keys(byMenu)) {
        if (normalizeCode(sectionName) === normalizeCode(urlCode)) {
          return normalizedGroup;
        }
      }
    }
  }

  return undefined;
}

const Menu = (props: MenuProps) => {
  const {
    userWorkooks,
    isSidebarClose,
    userTables,
    sectionTranscription,
    sectionRouteOverrides,
    hideMenuTitles = false,
    //isQuantilia,
  } = props;
  const { i18n } = useTranslation();
  const getWorkbookLabel = (
    workbookEntry: WorkbookInfo | string | undefined,
    fallback: string,
  ) => {
    if (!workbookEntry) return fallback;
    if (typeof workbookEntry === "string") return workbookEntry;
    return workbookEntry.label ?? fallback;
  };

  const getTableLabel = (
    tableEntry: TableInfo | string | undefined,
    fallback: string,
  ) => {
    if (!tableEntry) return fallback;
    if (typeof tableEntry === "string") {
      return i18n.t(tableEntry, {
        ns: "tables",
        tableEntry,
      });
    }
    return tableEntry.label ?? fallback;
  };

  const flattenWorkbooksByMenu = (data: MenuWorkbooks): WorkbooksByMenu => {
    const out: WorkbooksByMenu = {};
    for (const byWorkspace of Object.values(data)) {
      for (const byMenu of Object.values(byWorkspace)) {
        for (const [menuName, entries] of Object.entries(byMenu)) {
          out[menuName] = {
            ...(out[menuName] ?? {}),
            ...entries,
          };
        }
      }
    }
    return out;
  };

  const flattenTablesByMenu = (data: Tables): TablesByMenu => {
    const out: TablesByMenu = {};
    for (const byMenu of Object.values(data)) {
      for (const [menuName, entries] of Object.entries(byMenu)) {
        out[menuName] = {
          ...(out[menuName] ?? {}),
          ...entries,
        };
      }
    }
    return out;
  };

  const workbooksByMenu = useMemo(
    () => flattenWorkbooksByMenu(userWorkooks),
    [userWorkooks],
  );
  const tablesByMenu = useMemo(
    () => flattenTablesByMenu(userTables),
    [userTables],
  );

  const location = useLocation();
  const { current, setCurrent, available, uiSwitchTick } = useWorkspace();
  const routeContext = useMemo(
    () => parseWorkspaceContextFromPathname(location.pathname),
    [location.pathname],
  );
  const effectiveWorkspaceCode = routeContext?.workspace ?? current?.code;
  const effectiveGroup = routeContext?.group ?? current?.group;

  // Sync workspace from URL (URL has priority when not switched by the switcher)
  useEffect(() => {
    const urlCode =
      getWorkspaceCodeFromUrl?.(location.pathname, userWorkooks) ?? null;
    if (!urlCode || uiSwitchTick) return;
    const urlGroup = findUrlGroup(
      location.pathname,
      urlCode,
      userWorkooks,
      userTables,
    );

    const match = available.find(
      (w) => normalizeCode(w.code) === normalizeCode(urlCode),
    );

    const currentGroup = normalizeGroupKey(current?.group);
    const targetGroup = normalizeGroupKey(urlGroup ?? match?.group);
    const groupChanged = currentGroup !== targetGroup;
    const codeChanged =
      normalizeCode(current?.code) !== normalizeCode(match?.code);
    if (match && (codeChanged || groupChanged)) {
      setCurrent(
        { ...match, group: urlGroup ?? match.group },
        { source: "url" },
      ); // safe inside effect
    }
  }, [
    location.pathname,
    userWorkooks,
    userTables,
    available,
    current?.code,
    current?.group,
    setCurrent,
    uiSwitchTick,
  ]);

  useScrollMenuHeaderToTop();

  const [menuStates, setMenuStates] = useLocalStorage<MenuStatesType>(
    "sidebar-menu-states",
    {},
  );

  // While current.code is null (All), do NOT filter anything
  const filteredWorkbooks = useMemo(() => {
    if (!effectiveWorkspaceCode) return workbooksByMenu; // All -> keep the original
    const currentCode = normalizeCode(effectiveWorkspaceCode);
    const out: WorkbooksByMenu = {};
    const requestedGroups = effectiveGroup
      ? getMatchingGroupKeys(userWorkooks, effectiveGroup)
      : (Object.keys(userWorkooks) as Array<keyof MenuWorkbooks>);

    for (const groupKey of requestedGroups) {
      const byWorkspace = userWorkooks[groupKey] ?? {};
      for (const [workspaceName, byMenu] of Object.entries(byWorkspace)) {
        if (normalizeCode(workspaceName) === currentCode) {
          Object.assign(out, byMenu);
        }
      }
    }
    return out;
  }, [effectiveWorkspaceCode, effectiveGroup, userWorkooks, workbooksByMenu]);

  const filteredTables = useMemo(() => {
    if (!effectiveWorkspaceCode) return tablesByMenu; // All -> keep the original
    const currentCode = normalizeCode(effectiveWorkspaceCode);
    const out: typeof tablesByMenu = {};
    const requestedGroups = effectiveGroup
      ? getMatchingGroupKeys(userTables, effectiveGroup)
      : (Object.keys(userTables) as Array<keyof Tables>);

    for (const groupKey of requestedGroups) {
      const byMenu = userTables[groupKey] ?? {};
      for (const [section, list] of Object.entries(byMenu)) {
        const sectionCode = normalizeCode(section);
        if (sectionCode === currentCode) {
          out[section] = list;
        }
      }
    }
    return out;
  }, [effectiveWorkspaceCode, effectiveGroup, tablesByMenu, userTables]);

  useRedirectIfNotAllowed(
    filteredWorkbooks,
    filteredTables,
    sectionRouteOverrides,
  );

  const hasDashboards = Object.keys(filteredWorkbooks).length > 0;
  const hasAdmin = Object.keys(filteredTables).length > 0;
  const isK1Workspace =
    normalizeCode(effectiveWorkspaceCode ?? "") === normalizeCode("k1");
  const isR1Workspace =
    normalizeCode(effectiveWorkspaceCode ?? "") === normalizeCode("r1");

  // Flatten all admin items with their client prefix (used for K1 grouped layout)
  const k1FlatItems = useMemo(() => {
    if (!isK1Workspace) return null;
    const result: Record<string, { label: string; client: string }> = {};
    for (const [nameMenu, tables] of Object.entries(filteredTables)) {
      const client = sectionRouteOverrides[nameMenu] ?? nameMenu.toLowerCase();
      for (const [tableKey, tableEntry] of Object.entries(tables)) {
        const label =
          typeof tableEntry === "string"
            ? i18n.t(tableEntry, { ns: "tables", tableEntry })
            : ((tableEntry as TableInfo).label ?? tableKey);
        result[tableKey] = { label, client };
      }
    }
    return result;
  }, [isK1Workspace, filteredTables, sectionRouteOverrides, i18n]);

  // Flatten all admin items with their client prefix (used for R1 grouped layout)
  const r1FlatItems = useMemo(() => {
    if (!isR1Workspace) return null;
    const result: Record<string, { label: string; client: string }> = {};
    for (const [nameMenu, tables] of Object.entries(filteredTables)) {
      const client = sectionRouteOverrides[nameMenu] ?? nameMenu.toLowerCase();
      for (const [tableKey, tableEntry] of Object.entries(tables)) {
        const label =
          typeof tableEntry === "string"
            ? i18n.t(tableEntry, { ns: "tables", tableEntry })
            : ((tableEntry as TableInfo).label ?? tableKey);
        result[tableKey] = { label, client };
      }
    }
    return result;
  }, [isR1Workspace, filteredTables, sectionRouteOverrides, i18n]);

  const updateMenuState = (menuKey: string, isOpen: boolean) => {
    setMenuStates((prev) => ({
      ...prev,
      [menuKey]: isOpen,
    }));
  };

  return (
    <>
      {/* Dashboards group */}
      {hasDashboards && (
        <SidebarGroup>
          {/* Make the whole label the trigger row */}
          <SidebarGroupLabel className="px-2 py-1 tracking-widest font-medium text-gray-600 font-mono">
            <span>DASHBOARDS</span>
          </SidebarGroupLabel>
          <SidebarGroupContent className="px-0 py-1">
            <ul className="flex-1">
              {Object.entries(filteredWorkbooks).map(([menuName, menuItem]) => {
                const menuKey = `dashboard-${menuName}`;
                const workbookLabels = Object.fromEntries(
                  Object.entries(menuItem).map(([uuid, workbookEntry]) => [
                    uuid,
                    getWorkbookLabel(workbookEntry, uuid),
                  ]),
                );
                return (
                  <MenuItem
                    key={menuName}
                    module="dashboard"
                    menuName={menuName}
                    menuItem={workbookLabels}
                    isSidebarClose={isSidebarClose}
                    isOpen={menuStates[menuKey] ?? false}
                    onToggle={(open) => updateMenuState(menuKey, open)}
                    hideTitle={hideMenuTitles}
                  />
                );
              })}
            </ul>
          </SidebarGroupContent>
        </SidebarGroup>
      )}

      {/* K1 grouped layout */}
      {hasAdmin &&
        isK1Workspace &&
        k1FlatItems &&
        K1_GROUPS.map(({ label, routeItems }) => {
          const sortedItems = Object.fromEntries(
            routeItems
              .filter((key) => key in k1FlatItems)
              .map((key) => [key, k1FlatItems[key].label]),
          );
          if (Object.keys(sortedItems).length === 0) return null;
          const client =
            k1FlatItems[routeItems.find((k) => k in k1FlatItems)!]?.client ??
            "k1";
          return (
            <SidebarGroup key={label}>
              <SidebarGroupLabel className="px-2 py-1 tracking-widest font-medium text-gray-600 font-mono">
                <span>{label}</span>
              </SidebarGroupLabel>
              <SidebarGroupContent className="px-0 py-1">
                <SidebarMenu>
                  <SidebarMenuItem>
                    <Submenu
                      module="admin"
                      open={true}
                      menuItem={sortedItems}
                      isSidebarClose={false}
                      client={client}
                    />
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}

      {/* K1 ungrouped fallback — tables present in filteredTables but absent from K1_GROUPS */}
      {hasAdmin && isK1Workspace && k1FlatItems && (() => {
        const groupedKeys = new Set(K1_GROUPS.flatMap((g) => g.routeItems));
        const ungroupedEntries = Object.entries(k1FlatItems).filter(([key]) => !groupedKeys.has(key));
        if (ungroupedEntries.length === 0) return null;
        const menuItems = Object.fromEntries(ungroupedEntries.map(([key, val]) => [key, val.label]));
        const client = ungroupedEntries[0][1].client;
        return (
          <SidebarGroup>
            <SidebarGroupLabel className="px-2 py-1 tracking-widest font-medium text-gray-600 font-mono">
              <span>OTHER</span>
            </SidebarGroupLabel>
            <SidebarGroupContent className="px-0 py-1">
              <SidebarMenu>
                <SidebarMenuItem>
                  <Submenu
                    module="admin"
                    open={true}
                    menuItem={menuItems}
                    isSidebarClose={false}
                    client={client}
                  />
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        );
      })()}

      {/* R1 grouped layout */}
      {hasAdmin &&
        isR1Workspace &&
        r1FlatItems &&
        R1_GROUPS.map(({ label, routeItems }) => {
          const sortedItems = Object.fromEntries(
            routeItems
              .filter((key) => key in r1FlatItems)
              .map((key) => [key, r1FlatItems[key].label]),
          );
          if (Object.keys(sortedItems).length === 0) return null;
          const client =
            r1FlatItems[routeItems.find((k) => k in r1FlatItems)!]?.client ??
            "r1";
          return (
            <SidebarGroup key={label}>
              <SidebarGroupLabel className="px-2 py-1 tracking-widest font-medium text-gray-600 font-mono">
                <span>{label}</span>
              </SidebarGroupLabel>
              <SidebarGroupContent className="px-0 py-1">
                <SidebarMenu>
                  <SidebarMenuItem>
                    <Submenu
                      module="admin"
                      open={true}
                      menuItem={sortedItems}
                      isSidebarClose={false}
                      client={client}
                    />
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}

      {/* R1 ungrouped fallback — tables present in filteredTables but absent from R1_GROUPS */}
      {hasAdmin && isR1Workspace && r1FlatItems && (() => {
        const groupedKeys = new Set(R1_GROUPS.flatMap((g) => g.routeItems));
        const ungroupedEntries = Object.entries(r1FlatItems).filter(([key]) => !groupedKeys.has(key));
        if (ungroupedEntries.length === 0) return null;
        const menuItems = Object.fromEntries(ungroupedEntries.map(([key, val]) => [key, val.label]));
        const client = ungroupedEntries[0][1].client;
        return (
          <SidebarGroup>
            <SidebarGroupLabel className="px-2 py-1 tracking-widest font-medium text-gray-600 font-mono">
              <span>OTHER</span>
            </SidebarGroupLabel>
            <SidebarGroupContent className="px-0 py-1">
              <SidebarMenu>
                <SidebarMenuItem>
                  <Submenu
                    module="admin"
                    open={true}
                    menuItem={menuItems}
                    isSidebarClose={false}
                    client={client}
                  />
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        );
      })()}

      {/* Admin group (non-K1/R1 workspaces) */}
      {hasAdmin && !isK1Workspace && !isR1Workspace && (
        <SidebarGroup>
          <SidebarGroupLabel className="px-2 py-1 tracking-widest font-medium text-gray-600 font-mono">
            <span>ADMIN</span>
          </SidebarGroupLabel>
          <SidebarGroupContent className="px-0 py-1">
            <ul className="flex-1">
              {Object.entries(filteredTables).map(([nameMenu, tables]) => {
                const displayName = sectionTranscription[nameMenu] ?? nameMenu;
                const routeClient =
                  sectionRouteOverrides[nameMenu] ?? nameMenu.toLowerCase();
                const menuKey = `admin-${displayName}`;
                const tableLabels = Object.fromEntries(
                  Object.entries(tables).map(([tableKey, tableEntry]) => [
                    tableKey,
                    getTableLabel(tableEntry, tableKey),
                  ]),
                );
                return (
                  <MenuItem
                    key={nameMenu}
                    module="admin"
                    menuName={displayName}
                    menuItem={tableLabels}
                    isSidebarClose={isSidebarClose}
                    client={routeClient}
                    isOpen={menuStates[menuKey] ?? false}
                    onToggle={(open) => updateMenuState(menuKey, open)}
                    hideTitle={hideMenuTitles}
                  />
                );
              })}
            </ul>
          </SidebarGroupContent>
        </SidebarGroup>
      )}
    </>
  );
};

function hasActiveChild(
  module: "dashboard" | "admin",
  items: Record<string, string>,
  pathname: string,
  client?: string,
  workspace?: string | null,
  group?: string,
) {
  const safeDecode = (value: string) => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };
  const normalizeComparablePath = (value: string) => {
    const [path] = safeDecode(value).split(/[?#]/, 1);
    return path
      .split("/")
      .filter(Boolean)
      .map((segment) => normalizeCode(segment))
      .join("/");
  };
  const matchesNormalizedPath = (pathnameValue: string, target: string) => {
    const current = normalizeComparablePath(pathnameValue);
    const expected = normalizeComparablePath(target);
    return current === expected || current.startsWith(`${expected}/`);
  };

  const groupKey = normalizeWorkspaceGroupForPath(group);
  const workspaceCode = workspace ?? "";
  const normalizePathItem = (pathItem: string) => {
    if (!client) return pathItem;
    const prefixed = `${client}_`;
    return pathItem.startsWith(prefixed)
      ? pathItem.slice(prefixed.length)
      : pathItem;
  };

  return Object.keys(items).some((pathItem) => {
    const normalizedPathItem = normalizePathItem(pathItem);
    const adminBase =
      module === "admin"
        ? buildWorkspaceAdminPath({
            group: groupKey,
            workspace: workspaceCode,
          })
        : "";
    const to =
      module === "dashboard"
        ? buildWorkspaceDashboardPath({
            group: groupKey,
            workspace: workspaceCode,
            uuid: pathItem,
          })
        : buildWorkspaceAdminPath({
            group: groupKey,
            workspace: workspaceCode,
            path: client ? `${client}/${pathItem}` : pathItem,
          });
    const normalizedTo =
      module === "dashboard"
        ? to
        : buildWorkspaceAdminPath({
            group: groupKey,
            workspace: workspaceCode,
            path: client
              ? `${client}/${normalizedPathItem}`
              : normalizedPathItem,
          });
    const expandedTo =
      module === "admin" && client ? `${adminBase}/${client}/${pathItem}` : to;
    const expandedNormalizedTo =
      module === "admin" && client
        ? `${adminBase}/${client}/${normalizedPathItem}`
        : normalizedTo;

    // allow both direct and nested (e.g. /new, /:id)
    return (
      !!matchPath({ path: to, end: false }, pathname) ||
      !!matchPath({ path: `${to}/:rest/*`, end: false }, pathname) ||
      !!matchPath({ path: normalizedTo, end: false }, pathname) ||
      !!matchPath({ path: `${normalizedTo}/:rest/*`, end: false }, pathname) ||
      !!matchPath({ path: expandedTo, end: false }, pathname) ||
      !!matchPath({ path: `${expandedTo}/:rest/*`, end: false }, pathname) ||
      !!matchPath({ path: expandedNormalizedTo, end: false }, pathname) ||
      !!matchPath(
        { path: `${expandedNormalizedTo}/:rest/*`, end: false },
        pathname,
      ) ||
      matchesNormalizedPath(pathname, to) ||
      matchesNormalizedPath(pathname, normalizedTo) ||
      matchesNormalizedPath(pathname, expandedTo) ||
      matchesNormalizedPath(pathname, expandedNormalizedTo)
    );
  });
}

export type MenuItemType = {
  [uuid: string]: string;
};

type MenuItemProps = {
  module: string;
  menuName: string;
  menuItem: MenuItemType;
  isSidebarClose: boolean;
  client?: string;
  isOpen: boolean;
  onToggle: (isOpen: boolean) => void;
  hideTitle?: boolean;
};

export function MenuItem({
  module,
  menuName,
  menuItem,
  client,
  isOpen,
  onToggle,
  hideTitle = false,
}: MenuItemProps) {
  const { current } = useWorkspace();
  if (hideTitle) {
    // No header, always show the submenu
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          {/* a11y label so screen readers still announce the section */}
          <span className="sr-only">{menuName}</span>
          <div className="space-y-1">
            <Submenu
              module={module as "admin" | "dashboard"}
              open={true}
              menuItem={menuItem}
              isSidebarClose={false}
              client={client}
            />
          </div>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  const { pathname } = useLocation();

  const isActiveSection = hasActiveChild(
    module as "dashboard" | "admin",
    menuItem,
    pathname,
    client,
    current?.code ?? null,
    current?.group ?? DEFAULT_WORKSPACE_GROUP_KEY,
  );

  return (
    <SidebarMenu>
      <Collapsible
        open={isOpen}
        onOpenChange={onToggle}
        className="group/collapsible"
        asChild
      >
        <SidebarMenuItem data-menu-item>
          {/* Header row (acts as the toggle) */}
          <CollapsibleTrigger asChild>
            <SidebarMenuButton
              tooltip={menuName}
              className={cn(
                "w-full",
                isActiveSection &&
                  "relative bg-gray-100 before:absolute before:left-0 before:inset-y-1 before:w-0.5 before:bg-blue-500 before:rounded",
              )}
              data-menu-header
            >
              <span className="truncate text-xs">{menuName}</span>
              <ChevronDown className="ml-auto size-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
            </SidebarMenuButton>
          </CollapsibleTrigger>

          {/* Sub items */}
          <CollapsibleContent>
            <Submenu
              module={module as "admin" | "dashboard"}
              open={isOpen}
              menuItem={menuItem}
              isSidebarClose={false}
              client={client}
            />
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    </SidebarMenu>
  );
}

export default Menu;
